<template>
    <v-container fluid>
        <!--
          Super admin only. The API refuses every one of these calls for anyone
          else, so this guard is about not showing a broken page rather than being
          the security boundary - partners must not even be visible to an ordinary
          admin, and the sidebar entry is hidden from them too.
        -->
        <v-alert v-if="!isSuperAdmin" type="error" variant="tonal">
            You do not have access to this page.
        </v-alert>

        <template v-else>
            <v-card>
                <v-card-title class="d-flex align-center">
                    <span>Partners</span>
                    <v-spacer/>
                    <v-text-field v-model="search" density="compact" hide-details clearable
                                  prepend-inner-icon="mdi-magnify" label="Search name or code"
                                  style="max-width: 280px" class="mr-3" @update:modelValue="debouncedLoad"/>
                    <v-btn color="primary" prepend-icon="mdi-plus" @click="openCreate">Add partner</v-btn>
                </v-card-title>

                <v-card-text>
                    <v-alert v-for="(e, i) in errors" :key="i" type="error" variant="tonal" class="mb-3">{{ e }}</v-alert>

                    <v-data-table-server
                        :headers="headers"
                        :items="resellers"
                        :items-length="total"
                        :loading="loading"
                        :items-per-page="rows"
                        @update:options="onTableOptions">
                        <template #item.commission_percent="{ item }">
                            {{ formatPercent(item.commission_percent) }}
                        </template>
                        <template #item.status="{ item }">
                            <v-chip :color="item.status ? 'success' : 'grey'" size="small" variant="tonal">
                                {{ item.status ? 'Active' : 'Inactive' }}
                            </v-chip>
                        </template>
                        <template #item.actions="{ item }">
                            <v-btn icon="mdi-chart-line" size="small" variant="text" title="Commissions"
                                   @click="openCommissions(item)"/>
                            <v-btn icon="mdi-pencil" size="small" variant="text" title="Edit"
                                   @click="openEdit(item)"/>
                            <v-btn icon="mdi-delete" size="small" variant="text" title="Delete"
                                   @click="confirmDelete(item)"/>
                        </template>
                    </v-data-table-server>
                </v-card-text>
            </v-card>

            <ResellerFormDialog v-model="showForm" :reseller="editing" @saved="onSaved"/>
            <ResellerCommissionsDialog v-model="showCommissions" :reseller="viewing"/>

            <v-dialog v-model="showDelete" max-width="460">
                <v-card>
                    <v-card-title>Delete partner</v-card-title>
                    <v-card-text>
                        Delete <strong>{{ deleting?.name }}</strong>?
                        <!-- The API refuses while companies are still attached, because
                             commission rows reference the partner and a statement has to
                             stay readable after the partnership ends. -->
                        <div v-if="deleting?.companies_count" class="mt-2 text-error">
                            {{ deleting.companies_count }} company(ies) are still assigned. Unassign them first.
                        </div>
                    </v-card-text>
                    <v-card-actions>
                        <v-spacer/>
                        <v-btn variant="text" @click="showDelete = false">Cancel</v-btn>
                        <v-btn color="error" :loading="deletingBusy"
                               :disabled="!!deleting?.companies_count" @click="doDelete">Delete</v-btn>
                    </v-card-actions>
                </v-card>
            </v-dialog>
        </template>
    </v-container>
</template>

<script setup>
import {computed, ref} from 'vue';
import {send} from '@/composables/apiClient';
import {useAuthStore} from '@/stores/authStore';
import {useSnackbarStore} from '@/stores/snackBarStore';
import ResellerFormDialog from '@/components/dialogs/resellers/ResellerFormDialog.vue';
import ResellerCommissionsDialog from '@/components/dialogs/resellers/ResellerCommissionsDialog.vue';

const authStore = useAuthStore();
const {showSnackbar} = useSnackbarStore();

// Not type === 'super_admin': the session rewrites type to 'admin' and sets
// a separate super flag, so that comparison is never true.
const isSuperAdmin = computed(() => !!authStore.user?.super);

const headers = [
    {title: 'Code', key: 'code'},
    {title: 'Name', key: 'name'},
    {title: 'Commission', key: 'commission_percent'},
    {title: 'Payout', key: 'payout_period'},
    {title: 'Companies', key: 'companies_count'},
    {title: 'Status', key: 'status'},
    {title: '', key: 'actions', sortable: false, align: 'end'},
];

const resellers = ref([]);
const total = ref(0);
const page = ref(1);
const rows = ref(25);
const search = ref('');
const loading = ref(false);
const errors = ref([]);

const showForm = ref(false);
const editing = ref(null);
const showCommissions = ref(false);
const viewing = ref(null);
const showDelete = ref(false);
const deleting = ref(null);
const deletingBusy = ref(false);

/** Trailing zeros off, so 20.00 reads as 20% and 12.50 as 12.5%. */
const formatPercent = (v) => `${String(parseFloat(v ?? 0))}%`;

let searchTimer = null;
const debouncedLoad = () => {
    clearTimeout(searchTimer);
    searchTimer = setTimeout(() => {
        page.value = 1;
        load();
    }, 400);
};

const onTableOptions = ({page: p, itemsPerPage}) => {
    page.value = p;
    rows.value = itemsPerPage;
    load();
};

async function load() {
    if (!isSuperAdmin.value) return;
    loading.value = true;
    errors.value = [];
    try {
        const response = await send('get', 'admin/resellers', {
            page: page.value,
            rows: rows.value,
            search: search.value || undefined,
        });
        const data = response.data?.data;
        resellers.value = data?.data ?? [];
        total.value = data?.total ?? resellers.value.length;
    } catch (error) {
        errors.value.push(error.response?.data?.message || 'Could not load partners.');
    } finally {
        loading.value = false;
    }
}

const openCreate = () => {
    editing.value = null;
    showForm.value = true;
};
const openEdit = (reseller) => {
    editing.value = reseller;
    showForm.value = true;
};
const openCommissions = (reseller) => {
    viewing.value = reseller;
    showCommissions.value = true;
};
const confirmDelete = (reseller) => {
    deleting.value = reseller;
    showDelete.value = true;
};

const onSaved = () => {
    showForm.value = false;
    load();
};

async function doDelete() {
    deletingBusy.value = true;
    try {
        await send('delete', `admin/resellers/${deleting.value.id}`);
        showSnackbar('Partner deleted', 'success');
        showDelete.value = false;
        load();
    } catch (error) {
        showSnackbar(error.response?.data?.message || 'Could not delete partner', 'error');
    } finally {
        deletingBusy.value = false;
    }
}

load();
</script>
